import React, { useState, useRef, useCallback, useEffect, useContext } from "react";
import { CustomError } from "../../../utils/customeError";
import { ErrorCallback, FolderName, ProgressCallback, SKMediaType, uploadFile } from "../../../api/shared/uploadService";
import { UploadTask } from "firebase/storage";
import VideoPlayer from "../../../components/VideoPlayer";
import * as Yup from "yup";

const getVideoDuration = (file: File): Promise<number> => {
  return new Promise(resolve => {
    const video = document.createElement("video");
    video.preload = "metadata";

    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      resolve(video.duration);
    };

    video.src = URL.createObjectURL(file);
  });
};

interface VideoInputProps {
  onFileChange: (url: string | null) => void;
  videoUrl?: string;
  maxVideoDuration?: number;
  maxFileSize?: number;
  docId: string;
  editable?: boolean;
  folder: FolderName;
}

const VideoInput = ({ docId, onFileChange, videoUrl, maxVideoDuration = 90, maxFileSize = 10, editable = true, folder }: VideoInputProps) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [videoTask, setVideoTask] = useState<UploadTask | null>(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoDimensions, setVideoDimensions] = useState<{
    width: number;
    height: number;
  }>({ width: 150, height: 200 });

  const videoSchema = Yup.object().shape({
    videoFile: Yup.mixed<File>()
      .test("fileSize", `File Size should be below ${maxFileSize} MB`, value => {
        if (!value) return false;
        const fileSize = value.size;
        return fileSize <= maxFileSize * 1024 * 1024; // 50MB limit
      })
      .test("videoDuration", `Video duration should be under ${maxVideoDuration} second`, async value => {
        if (!value) return false;
        const videoDuration = await getVideoDuration(value);
        return videoDuration <= maxVideoDuration;
      }),
  });

  useEffect(() => {
    if (videoUrl) {
      const video = document.createElement("video");
      video.src = videoUrl;
      video.onloadedmetadata = () => {
        const aspectRatio = video.videoWidth / video.videoHeight;
        let newWidth = 290;
        let newHeight = Math.min(200, newWidth / aspectRatio);

        if (newHeight > 200) {
          newHeight = 200;
          newWidth = newHeight * aspectRatio;
        }

        setVideoDimensions({ width: newWidth, height: newHeight });
      };
    }
  }, [videoUrl]);

  const progressCallback: ProgressCallback = (progress: number, mediaType: SKMediaType) => {
    console.log(`Upload progress: ${progress}%`);
  };

  const errorCallback: ErrorCallback = (error: Error, mediaType: SKMediaType) => {
    console.error(`Upload failed: ${error.message}`);
    setUploadingVideo(false);
  };

  const successCallback = useCallback(
    (url: string, mediaType: SKMediaType) => {
      setUploadingVideo(false);
      onFileChange(url);
    },
    [onFileChange]
  );

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      try {
        const fileUrl = URL.createObjectURL(file);
        await videoSchema.validate({ videoFile: file }, { abortEarly: false });
        setErrorMessage("");
        //setImagePreview(fileUrl);
        setUploadingVideo(true);

        const video = document.createElement("video");
        video.src = fileUrl;
        video.onloadedmetadata = () => {
          const aspectRatio = video.videoWidth / video.videoHeight;
          let newWidth = 290;
          let newHeight = Math.min(200, newWidth / aspectRatio);

          if (newHeight > 200) {
            newHeight = 200;
            newWidth = newHeight * aspectRatio;
          }

          setVideoDimensions({ width: newWidth, height: newHeight });
        };

        let task = uploadFile(file, docId, SKMediaType.video, progressCallback, errorCallback, successCallback, folder);
        setVideoTask(task);
      } catch (err) {
        let error = err as Error;
        setErrorMessage(error.message);
        setUploadingVideo(false);
      }
    },
    [successCallback, docId, videoSchema]
  );

  const removeVideo = () => {
    onFileChange(null);
    setImagePreview(null);
    setVideoDimensions({ width: 150, height: 200 });
    setUploadingVideo(false);
    videoTask?.cancel();
    setVideoTask(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="flex flex-col w-full max-w-[290px]">
      <div
        className="relative image-input"
        style={{
          width: `${videoDimensions.width}px`,
          height: `${videoDimensions.height}px`,
        }}
        onClick={() => {
          if (editable) {
            fileInputRef?.current?.click();
          }
          //editable && fileInputRef?.current?.click();
        }}
      >
        {!uploadingVideo && videoUrl && editable && (
          <button
            id="clsbtn"
            className="absolute top-2 right-2 rounded-full p-[1px] shadow-md hover:bg-gray-200 z-10"
            onClick={e => {
              e.stopPropagation();
              removeVideo();
            }}
          >
            <img className="w-[20px] h-[20px]" src="/icons/CloseButton.png" alt="remove" />
          </button>
        )}

        <input
          name="videoFile"
          type="file"
          ref={fileInputRef}
          accept={".mp4,.MP4,.mov,.MOV,.m4v,.M4V"}
          onChange={handleFileChange}
          className="hidden"
          id={`file-upload-video`}
          disabled={!editable}
        />

        {uploadingVideo && (
          <div className="flex flex-col items-center justify-center p-4 space-y-4 bg-white shadow-sm rounded-2xl">
            <img src={"/icons/mediaupload.gif"} alt="Saving" className="w-[25px] h-[25px]" />
            <div className="font-bold text-center text-gray-400 w-[69px] h-[16px]">Uploading...</div>
            <button
              className="text-black border border-gray-400 rounded-md w-[53px] h-[30px]"
              onClick={e => {
                e.stopPropagation();
                setImagePreview(null);
                setUploadingVideo(false);
                videoTask && videoTask.cancel();
              }}
            >
              Cancel
            </button>
          </div>
        )}

        {!uploadingVideo && videoUrl && (
          <div className="w-full h-full overflow-hidden">
            <VideoPlayer videoUrl={videoUrl} />
          </div>
        )}

        {!(uploadingVideo || videoUrl) && (
          <label htmlFor="file-upload" className="cursor-pointer">
            <div className="flex flex-col items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-[27px] h-[30px] mb-[5px] text-[#8E8E93]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
              </svg>
              <span className="image-input-media-type">MP4, MOV, or M4V</span>
            </div>
          </label>
        )}
      </div>

      {errorMessage && <p className="mt-2 text-sm text-red-500">{errorMessage}</p>}
    </div>
  );
};

export default VideoInput;
